import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  Progress,
  Spin,
  Avatar,
  Tooltip,
} from "antd";
import "./PollBox.scss";
import { useInfoUser } from "../../store/UserStore";
import { VotedModal } from "../modal/chat/VotedModal";
import apiFactory from "../../api";
import { useImageLoader } from "../customHook/ImageLoader";
import { getColorFromInitial } from "../../utils/Utils";
import { last } from "lodash";
import VoterTabsModal from "../modal/chat/VoterTabsModal";

const { Title, Text } = Typography;

const VoterAvatarSmall = ({ voter }) => {
  const {
    imageUrl,
    loading: isImageLoading,
    error: imageError,
  } = useImageLoader(voter?.avatar);

  const [briefName, setBriefName] = useState("");

  useEffect(() => {
    setBriefName(
      last(voter?.name?.trim()?.split(" "))?.[0] ||
        last(voter?.username?.trim()?.split(" "))?.[0] ||
        "U"
    );
  }, [voter]);

  return (
    <Tooltip title={voter?.name || voter?.username}>
      {isImageLoading ? (
        <Avatar className="voter-avatar">
          <Spin size="small" />
        </Avatar>
      ) : !imageError ? (
        <Avatar
          className="voter-avatar"
          src={imageUrl}
          alt={voter?.name?.[0]?.toUpperCase()}
          style={{
            backgroundColor: getColorFromInitial(briefName),
          }}
        >
          {voter?.name?.[0]?.toUpperCase()}
        </Avatar>
      ) : (
        <Avatar
          className="voter-avatar"
          style={{
            backgroundColor: getColorFromInitial(briefName),
          }}
        >
          {voter?.name?.[0]?.toUpperCase()}
        </Avatar>
      )}
    </Tooltip>
  );
};

const VoterAvatarList = ({ voters = [], totalCount = 0 }) => {
  if (totalCount === 0) {
    return <span>0</span>;
  }

  const hiddenCount = totalCount - voters.length;

  return (
    <div className="voter-avatar-list-container">
      {voters?.map((voter) => (
        <VoterAvatarSmall voter={voter} key={voter.userId} />
      ))}

      {hiddenCount > 0 && (
        <Tooltip title={`${hiddenCount} Other voters`}>
          <Avatar className="voter-avatar more-avatar">+{hiddenCount}</Avatar>
        </Tooltip>
      )}
    </div>
  );
};

const PollOption = ({
  option,
  totalVotes,
  hasVoted,
  hideVoters,
  voterDetailsCache,
  isLoading,
  shouldHideResultsFromUser,
  isOwner,
}) => {
  const voteCount = option?.userIds?.length || 0;
  const percentage = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;

  const showResults = isOwner ? true : !shouldHideResultsFromUser;
  const showVoters = isOwner ? true : !hideVoters;

  const colors = {
    selected: { stroke: "#93c5fd", trail: "#eff6ff" },
    default: { stroke: "#d1d5db", trail: "#f3f4f6" },
  };

  const currentColors = hasVoted ? colors.selected : colors.default;

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    const isUpperCase = text === text?.toUpperCase();
    if (isUpperCase || (!text?.includes(" ") && text?.length > maxLength)) {
      maxLength = maxLength - 5;
    }
    if (text?.length <= maxLength) {
      return text;
    }
    let truncated = text?.substring(0, maxLength);
    const lastSpace = truncated?.lastIndexOf(" ");
    if (lastSpace > 0) {
      truncated = truncated?.substring(0, lastSpace);
    }
    return truncated + " ...";
  };

  const voterList = useMemo(() => {
    const firstTenUserIds = option?.userIds?.slice(0, 10) || [];
    return firstTenUserIds
      ?.map((id) => voterDetailsCache?.[id])
      .filter(Boolean);
  }, [option?.userIds, voterDetailsCache]);

  return (
    <>
      <div className="poll-option-wrapper">
        {isLoading && voterList?.length === 0 && (
          <div className="poll-loading-overlay">
            <Spin />
          </div>
        )}
        <Progress
          percent={showResults ? percentage : 0}
          strokeColor={currentColors.stroke}
          trailColor={currentColors.trail}
          showInfo={false}
        />
        <div className="poll-option-content">
          {option?.optionText?.length > 35 ? (
            <Tooltip title={option?.optionText}>
              <span className="option-text">
                {truncateText(option?.optionText, 35)}
              </span>
            </Tooltip>
          ) : (
            <span className="option-text">{option?.optionText}</span>
          )}
          <span className="vote-count">
            {showVoters ? (
              <VoterAvatarList
                voters={showResults ? voterList : []}
                totalCount={showResults ? voteCount : 0}
              />
            ) : (
              <Tooltip title="The number of voters has been hidden by the creator.">
                <span>Hidden</span>
              </Tooltip>
            )}
          </span>
        </div>
      </div>
    </>
  );
};

const PollBox = ({ voteMessageData, conversationId }) => {
  const { user } = useInfoUser();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isVoterInfoModalOpen, setIsVoterInfoModalOpen] = useState(false);
  const [isVoteModalOpen, setIsVoteModalOpen] = useState(false);

  const [voterDetailsCache, setVoterDetailsCache] = useState(() => {
    if (user?.userId) {
      return { [user.userId]: user };
    }
    return {};
  });
  const [isLoadingVoters, setIsLoadingVoters] = useState(false);

  const hideVoters = voteMessageData?.hideVoters || false;
  const allOptions = voteMessageData?.voteOptions || [];

  const isOwner = useMemo(() => {
    return user?.userId === voteMessageData?.senderId;
  }, [user?.userId, voteMessageData?.senderId]);

  const userHasVoted = useMemo(() => {
    return (
      voteMessageData?.voteOptions?.some((option) =>
        option.userIds?.includes(user?.userId)
      ) || false
    );
  }, [voteMessageData, user]);

  const shouldHideResultsFromUser =
    voteMessageData?.hideResults && !userHasVoted;

  const voteStats = useMemo(() => {
    if (!allOptions.length) {
      return { totalVotes: 0, uniqueVoters: 0 };
    }
    let total = 0;
    const voters = new Set();
    allOptions.forEach((option) => {
      total += option?.userIds?.length || 0;
      option?.userIds?.forEach((id) => voters?.add(id));
    });
    return { totalVotes: total, uniqueVoters: voters.size };
  }, [allOptions]);

  const formattedVoteData = useMemo(() => {
    if (!voteMessageData?.voteOptions) {
      return [];
    }
    return voteMessageData.voteOptions.map((option) => ({
      optionId: option.voteOptionId,
      optionName: option.optionText,
      userIds: option?.userIds || [],
    }));
  }, [voteMessageData?.voteOptions]);

  const formattedDeadline = useMemo(() => {
    if (!voteMessageData?.deadline) return null;
    const date = new Date(voteMessageData.deadline);
    const timeString = date.toLocaleTimeString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
    });
    const dateString = date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    return `Completed at ${timeString} ${dateString}`;
  }, [voteMessageData?.deadline]);

  const hiddenOptionsCount = allOptions.length > 3 ? allOptions.length - 3 : 0;
  const displayedOptions = isExpanded ? allOptions : allOptions?.slice(0, 3);

  const toggleExpand = () => setIsExpanded((prev) => !prev);

  const handleOpenVoterInfoModalOpen = () => {
    if (!isOwner && (hideVoters || shouldHideResultsFromUser)) return;
    setIsVoterInfoModalOpen(true);
  };

  useEffect(() => {
    const fetchVoterDetails = async () => {
      const allUserIds = new Set();
      allOptions.forEach((option) => {
        option?.userIds?.slice(0, 3).forEach((id) => allUserIds.add(id));
      });

      if (allUserIds.size === 0) return;

      const idsToFetch = Array.from(allUserIds).filter(
        (id) => !voterDetailsCache[id]
      );

      if (idsToFetch.length === 0) return;

      try {
        setIsLoadingVoters(true);
        const rs = await apiFactory.userApi.listUserById(idsToFetch);

        if (rs?.status === 200 && rs.data) {
          const newVoterDetails = {};
          rs.data.forEach((voter) => {
            newVoterDetails[voter.userId] = voter;
          });
          setVoterDetailsCache((prevCache) => ({
            ...prevCache,
            ...newVoterDetails,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch voter details:", error);
      } finally {
        setIsLoadingVoters(false);
      }
    };

    fetchVoterDetails();
  }, [allOptions, voterDetailsCache]);

  return (
    <>
      <Card style={{ width: 350 }} bodyStyle={{ padding: "16px" }}>
        <div className="m-1">
          <div className="poll-header">
            <Title level={5} style={{ margin: 0 }}>
              {voteMessageData?.voteTitle}
            </Title>
            {formattedDeadline && (
              <Text type="secondary">{formattedDeadline}</Text>
            )}
          </div>

          <Typography.Link
            className="poll-voter-info"
            onClick={handleOpenVoterInfoModalOpen}
          >
            {voteStats.uniqueVoters} Voters
          </Typography.Link>

          <Space
            direction="vertical"
            size={0}
            style={{ width: "100%", marginTop: "6px" }}
          >
            {displayedOptions.map((option) => {
              const hasVoted = option?.userIds?.includes(user?.userId);

              return (
                <PollOption
                  key={option.voteOptionId}
                  option={option}
                  totalVotes={voteStats.totalVotes}
                  hasVoted={hasVoted}
                  hideVoters={hideVoters}
                  voterDetailsCache={voterDetailsCache}
                  isLoading={isLoadingVoters}
                  shouldHideResultsFromUser={shouldHideResultsFromUser}
                  isOwner={isOwner}
                />
              );
            })}

            {hiddenOptionsCount > 0 && (
              <span className="show-more-button" onClick={toggleExpand}>
                {isExpanded ? "Hide" : `+ ${hiddenOptionsCount} other options`}
              </span>
            )}
          </Space>

          <Button
            onClick={() => setIsVoteModalOpen(true)}
            block
            style={{ marginTop: "16px" }}
          >
            Vote or change
          </Button>
        </div>
      </Card>

      {isVoterInfoModalOpen && (
        <VoterTabsModal
          voteData={formattedVoteData}
          open={isVoterInfoModalOpen}
          title={"Voters"}
          onCancel={() => setIsVoterInfoModalOpen(false)}
        />
      )}
      {isVoteModalOpen && (
        <VotedModal
          open={isVoteModalOpen}
          voteMessageData={voteMessageData}
          onCancel={() => setIsVoteModalOpen(false)}
          conversationId={conversationId}
          shouldHideResultsFromUser={shouldHideResultsFromUser}
          hideVoters={hideVoters}
          voterDetailsCache={voterDetailsCache}
          isOwner={isOwner}
        />
      )}
    </>
  );
};

export default PollBox;
